import { Box, Button, Flex, Text, Theme } from "@radix-ui/themes";
import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { PanelRightClose, X } from "lucide-react";
import { Row } from "@tanstack/react-table";
import { TrackManagerApplication } from "@/types/shared";
import ApplicationsTable from "@/shared/tables/track_manager/ApplicationsTable";
import ApplicationReviewForm from "@/shared/track_manager/ApplicationReviewForm";

interface Props {
  applications: TrackManagerApplication[];
  /** Shown in place of the table when there is nothing to review. Each caller
   *  knows why its own list is empty; this component doesn't. */
  emptyMessage: string;
  /** `"event"` drops the columns that repeat on a single event's page. */
  scope?: "track" | "event";
}

/**
 * The applications table plus the slide-in review drawer and its Previous/Next
 * paging. Desktop only — the mobile path is `ApplicationsCards`.
 *
 * Shared by the cross-event queue (`track_manager/applications/index`) and one
 * event's page (`manager/events/show`), so a change to how review works lands
 * on both.
 */
export default function ApplicationQueue({
  applications,
  emptyMessage,
  scope = "track",
}: Props) {
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [filteredRows, setFilteredRows] = useState<
    Row<TrackManagerApplication>[] | null
  >();

  if (applications.length === 0) {
    return <Text color="gray">{emptyMessage}</Text>;
  }

  return (
    <Dialog.Root
      open={selectedRowIndex !== null}
      onOpenChange={(open) => {
        if (!open) setSelectedRowIndex(null);
      }}
    >
      <Box style={{ overflowX: "auto" }}>
        <ApplicationsTable
          applications={applications}
          setSelectedRowIndex={setSelectedRowIndex}
          setFilteredRows={setFilteredRows}
          scope={scope}
        />
      </Box>

      <Dialog.Portal>
        <Theme>
          <Dialog.Overlay
            style={{
              position: "fixed",
              inset: 0,
              background: "var(--color-overlay)",
            }}
          />
          <Dialog.Content
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "min(480px, 100vw)",
              height: "100vh",
              backgroundColor: `var(--color-panel-solid)`,
              padding: "var(--space-5)",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-4)",
            }}
          >
            <Box p="4" style={{ backgroundColor: "var(--color-panel-solid)" }}>
              <Flex justify="between" align="center" mb="4">
                <Dialog.Title asChild>
                  <Text size="5" weight="bold">
                    Application Detail
                  </Text>
                </Dialog.Title>
                <Dialog.Close asChild>
                  <Button variant="ghost" size="1">
                    <X size="16px" />
                  </Button>
                </Dialog.Close>
              </Flex>
              {selectedRowIndex !== null &&
                filteredRows?.[selectedRowIndex] && (
                  <ApplicationReviewForm
                    application={filteredRows[selectedRowIndex].original}
                  />
                )}
              <Flex justify="between" align="center" pt="2">
                <Button
                  variant="soft"
                  disabled={selectedRowIndex === null || selectedRowIndex <= 0}
                  onClick={() =>
                    setSelectedRowIndex((i) => (i !== null ? i - 1 : null))
                  }
                >
                  Previous
                </Button>
                <Text size="1" color="gray">
                  {selectedRowIndex !== null
                    ? `${selectedRowIndex + 1} / ${filteredRows?.length}`
                    : ""}
                </Text>
                <Button
                  variant="soft"
                  disabled={
                    selectedRowIndex === null ||
                    selectedRowIndex >= (filteredRows?.length ?? 1) - 1
                  }
                  onClick={() =>
                    setSelectedRowIndex((i) => (i !== null ? i + 1 : null))
                  }
                >
                  Next
                </Button>
              </Flex>
            </Box>
            <Dialog.Close asChild>
              <Button
                variant="soft"
                size="3"
                className="sm:hidden"
                style={{
                  position: "sticky",
                  bottom: 0,
                  alignSelf: "flex-end",
                  marginTop: "auto",
                  backgroundColor: "var(--color-panel-solid)",
                }}
              >
                <PanelRightClose size="16px" />
                Close
              </Button>
            </Dialog.Close>
          </Dialog.Content>
        </Theme>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
